import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';

import BackendService from 'BackendService';
import { StyledButton, StyledButtonGroup } from '../components/styles/StyledButtons';
import StyledSection from '../components/styles/StyledSection';
import {
  Root,
  StyledHeading,
  StyledTextField,
  StyledTypography,
} from '../components/styles/styles';

interface State {
  loading: boolean;
  invitationValid?: boolean;
  firstName?: string;
  password: string;
  confirmPassword: string;
  error: string | null;
  passwordVisible: boolean;
  submitting: boolean;
  complete: boolean;
}

export default class CreateUser extends React.Component<{ invitationToken: string }, State> {
  constructor(props: { invitationToken: string }) {
    super(props);
    this.state = {
      loading: true,
      password: '',
      confirmPassword: '',
      error: null,
      passwordVisible: false,
      submitting: false,
      complete: false,
    };
  }

  componentDidMount(): void {
    if (!this.props.invitationToken) {
      this.setState({
        loading: false,
        invitationValid: false,
        error: 'This link seems to be invalid, please check link or contact support for help',
      });
      return;
    }

    if (this.state.loading) {
      new BackendService()
        .validateInvitationToken(this.props.invitationToken)
        .then(({ valid, firstName }) => {
          this.setState({ loading: false, invitationValid: valid, firstName });
        })
        .catch((err: Error) => {
          let error = 'Unable to verify link, please contact support';
          if (err.message === 'EXPIRED') {
            error = 'This link has expired, please contact support to get a new link';
          }
          this.setState({ loading: false, invitationValid: false, error });
        });
    }
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    this.setState({ ...this.state, [name]: value });
  };

  togglePasswordVisibility = () => {
    this.setState((prevState) => ({
      passwordVisible: !prevState.passwordVisible,
    }));
  };

  handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const { password, confirmPassword } = this.state;

    if (password !== confirmPassword) {
      this.setState({ error: 'Passwords do not match' });
      return;
    }

    this.setState({ submitting: true });
    new BackendService()
      .createUserWithEmailAndPassword(this.props.invitationToken, password)
      .then(() => {
        this.setState({ submitting: false, complete: true });
        setTimeout(() => {
          window.location.href = '/login';
        }, 2500);
      })
      .catch((err) => {
        this.setState({
          error: err.message || 'An error occurred',
          submitting: false,
        });
      });
  };

  render() {
    const {
      loading,
      invitationValid,
      firstName,
      password,
      confirmPassword,
      error,
      passwordVisible,
      submitting,
      complete,
    } = this.state;

    if (loading) {
      return (
        <Root>
          <StyledSection tone="tone1">
            <Typography>Verifying link...</Typography>
          </StyledSection>
        </Root>
      );
    }

    if (!invitationValid) {
      return (
        <Root>
          <StyledSection tone="tone1">
            <Typography color="error">{error}</Typography>
          </StyledSection>
        </Root>
      );
    }

    if (complete) {
      return (
        <Root>
          <StyledSection tone="tone1">
            <StyledTypography>Account created! Redirecting to login page...</StyledTypography>
          </StyledSection>
        </Root>
      );
    }

    return (
      <Root>
        <StyledSection tone="tone1">
          <StyledHeading>Welcome {firstName},</StyledHeading>
          <StyledHeading headerVariant="subheading">
            To complete signup please set a password.
          </StyledHeading>
          <form onSubmit={this.handleSubmit}>
            <StyledTextField
              label="Password"
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              onChange={this.handleChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={this.togglePasswordVisibility} edge="end">
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              label="Confirm Password"
              name="confirmPassword"
              type={passwordVisible ? 'text' : 'password'}
              value={confirmPassword}
              onChange={this.handleChange}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={this.togglePasswordVisibility} edge="end">
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <StyledTypography color="error">{error}</StyledTypography>}
            <StyledButtonGroup>
              <StyledButton type="submit" variant="contained">
                {submitting ? (
                  <CircularProgress size={24} style={{ margin: 'auto' }} />
                ) : (
                  'Create Account'
                )}
              </StyledButton>
            </StyledButtonGroup>
          </form>
        </StyledSection>
      </Root>
    );
  }
}
